import Vue from 'vue'
import { S, G, M, A } from './types'
import { Getters, Mutations, Actions } from 'vuex'
import queryString from 'query-string'
import dayjs from 'dayjs'
import { DateString } from '@/types/utils'
import { DateCallLog } from '@/types/call'
import { LocalStorageKeys } from '@/constants/localStorage'
import { extractCursor } from '@/api/util'
import { SynchronizedStorage } from '@/synchronizedStorage'

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  userselectModal: false,
  history: {
    logs: [],
    nextCursor: ''
  },
  missedHistory: {
    logs: []
  },
  callPartners: [],
  lastCheckedTime: null
})

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  callLog: ({ history }) => history.logs,
  missedCallLog: ({ missedHistory }) => missedHistory.logs,
  dateCallLogs: state => {
    const logs = state.history.logs
    const days = logs.reduce((days, log) => {
      const startDate = dayjs(log.registeredDatetime).format('YYYY-MM-DD')
      if (days.indexOf(startDate) < 0) days.push(startDate)
      return days
    }, [] as string[])

    return days.reduce((dateLogs, day) => {
      const date = dayjs(day).format('YYYY-MM-DD') as DateString
      dateLogs.push({
        date,
        logs: logs.filter(
          log => dayjs(log.registeredDatetime).format('YYYY-MM-DD') === day
        )
      })
      return dateLogs
    }, [] as DateCallLog[])
  },
  hasHistoryNext: state => {
    return !!state.history.nextCursor
  },
  callPartners: state => {
    return state.callPartners
  },
  userselectModal: state => {
    return state.userselectModal
  },
  lastCheckedTime: ({ lastCheckedTime }) => {
    if (lastCheckedTime !== null) {
      return lastCheckedTime
    }

    const timestamp = localStorage.getItem(
      LocalStorageKeys.CALL_CHECKED_DATETIME
    )

    if (timestamp === null) {
      return timestamp
    }

    return Number(timestamp)
  }
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  displayUserselectModal: (state, display) => {
    state.userselectModal = display
  },
  updateHistory: (state, logs) => {
    state.history = {
      ...state.history,
      logs: logs
    }
  },
  updateMissedHistory: (state, logs) => {
    state.missedHistory = { logs }
  },
  addHistory: (state, logs) => {
    state.history = {
      ...state.history,
      logs: [...state.history.logs, ...logs]
    }
  },
  updateHistoryNext: (state, next) => {
    state.history = {
      ...state.history,
      nextCursor: extractCursor(next)
    }
  },
  updateCallPartners: (state, users) => {
    state.callPartners = users
  },
  addCallPartners: (state, users) => {
    const ids = state.callPartners.map(user => user.id)
    const newUsers = users.filter(user => !ids.includes(user.id))
    state.callPartners = [...state.callPartners, ...newUsers]
  },
  removeCallPartners: (state, users) => {
    const ids = users.map(user => user.id)
    state.callPartners = state.callPartners.filter(
      user => !ids.includes(user.id)
    )
  },
  setLastCheckedTime: (state, timestamp) => {
    state.lastCheckedTime = timestamp
    SynchronizedStorage.MessageLastCheckedTime.set(String(timestamp))
  }
}

/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  displayUserselectModal: ({ commit }, display) => {
    commit('displayUserselectModal', display)
  },
  updateHistory: async ({ commit }, payload = {}) => {
    const res = await Vue.$api.call.history(payload)
    commit('updateHistory', res.results)
    commit('updateHistoryNext', res.next || '')
  },
  updateMissedHistory: async ({ commit }) => {
    const res = await Vue.$api.call.history({ isMissed: true })
    commit('updateMissedHistory', res.results)
  },
  loadNextHistory: async ({ commit, state }) => {
    const cursor = state.history.nextCursor
    if (!cursor) return
    const res = await Vue.$api.call.history({ cursor })
    commit('addHistory', res.results)
    commit('updateHistoryNext', res.next || '')
  },
  updateCallPartners: ({ commit }, users) => {
    commit('updateCallPartners', users)
  },
  addCallPartners: ({ commit }, users) => {
    commit('addCallPartners', users)
  },
  removeCallPartners: ({ commit }, users) => {
    commit('removeCallPartners', users)
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
